import {
  Table,
  Button,
  Tag,
  Space,
  Popconfirm,
  message,
  Select,
  Tooltip,
} from "antd";
import moment from "moment";
import React, { useState, useMemo, useCallback } from "react";
import { useQueryClient } from "react-query";
import {
  useGetInstructorDevices,
  useUpdateDeviceStatus,
  useGetDeviceAudit,
} from "../../../../../services/instructorDevice.service";
import { useStaffGroup } from "./StaffGroupContext";

const { Option } = Select;

const AUDIT_LABELS = {
  auto_registered: { text: "Auto-registered", color: "blue" },
  requested: { text: "Requested", color: "orange" },
  blocked: { text: "Blocked", color: "red" },
  approved: { text: "Approved", color: "green" },
  rejected: { text: "Rejected", color: "red" },
  revoked: { text: "Revoked", color: "default" },
};

const AUDIT_REASONS = {
  device_not_registered: "Device not registered to this instructor",
  device_belongs_to_another_instructor:
    "Device belongs to another instructor",
  auto_registered: "First device — bound automatically",
  device_pending_approval: "Awaiting admin approval",
  superseded_by_approved_device: "Superseded by a newly approved device",
};

const STATUS_COLORS = {
  active: "green",
  pending: "orange",
  rejected: "red",
  revoked: "default",
};

const STATUS_LABELS = {
  active: "Active",
  pending: "Pending",
  rejected: "Rejected",
  revoked: "Revoked",
};

/** Device ids are UUIDs — show enough to tell two apart without the noise. */
const shortId = (id) => (id ? `${String(id).slice(0, 8)}…` : "-");

const Devices = () => {
  const group = useStaffGroup();
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState("pending");

  const params = useMemo(
    () => ({ ...(statusFilter ? { status: statusFilter } : {}), group }),
    [statusFilter, group]
  );

  const { data: devicesData, isLoading } = useGetInstructorDevices({}, params);

  const data = useMemo(() => devicesData || [], [devicesData]);

  const { data: auditData, isLoading: auditLoading } = useGetDeviceAudit(
    {},
    { limit: 100, group }
  );

  const auditRows = useMemo(() => auditData || [], [auditData]);

  const updateDevice = useUpdateDeviceStatus({
    onSuccess: (response, variables) => {
      const statusText =
        variables.data.status === "approved" ? "approved" : "rejected";
      message.success(`Device ${statusText} successfully`);
      queryClient.invalidateQueries("instructorDevices");
      queryClient.invalidateQueries("instructorDeviceAudit");
    },
    onError: (error) => {
      message.error(
        error?.response?.data?.message || "Failed to update the device"
      );
    },
  });

  const handleApprove = useCallback(
    (id) => {
      updateDevice.mutate({ deviceId: id, data: { status: "approved" } });
    },
    [updateDevice]
  );

  const handleReject = useCallback(
    (id) => {
      updateDevice.mutate({ deviceId: id, data: { status: "rejected" } });
    },
    [updateDevice]
  );

  const columns = useMemo(
    () => [
      {
        title: "Instructor",
        dataIndex: "instructor_name",
        key: "instructor",
        align: "center",
        render: (text) => <div className="capitalize">{text || "Unknown"}</div>,
      },
      {
        title: "Device",
        key: "device",
        align: "center",
        render: (_, record) => (
          <div className="flex flex-col">
            <span className="font-medium">
              {[record.platform, record.model].filter(Boolean).join(" ") ||
                "Unknown device"}
            </span>
            <Tooltip title={record.device_id}>
              <span className="text-xs text-gray-400 cursor-help">
                {shortId(record.device_id)}
              </span>
            </Tooltip>
          </div>
        ),
      },
      {
        title: "Requested",
        dataIndex: "createdAt",
        key: "createdAt",
        align: "center",
        render: (text) => (
          <span className="text-gray-600">
            {text ? moment(text).format("DD/MM/YYYY hh:mm A") : "-"}
          </span>
        ),
      },
      {
        title: "Last Used",
        dataIndex: "last_used_at",
        key: "last_used_at",
        align: "center",
        render: (text) => (
          <span className="text-gray-600">
            {text ? moment(text).format("DD/MM/YYYY hh:mm A") : "-"}
          </span>
        ),
      },
      {
        title: "Status",
        key: "status",
        align: "center",
        render: (_, record) => (
          <div className="flex flex-col items-center gap-1">
            <Tag
              color={STATUS_COLORS[record.status] || "default"}
              className="capitalize px-3 py-1"
            >
              {STATUS_LABELS[record.status] || record.status}
            </Tag>
            {record.auto_registered && record.status === "active" && (
              <Tooltip title="Bound automatically as this instructor's first device">
                <span className="text-xs text-gray-400">auto-registered</span>
              </Tooltip>
            )}
          </div>
        ),
      },
      {
        title: "Actions",
        key: "actions",
        align: "center",
        render: (_, record) => {
          if (record.status !== "pending") {
            return (
              <Tag
                color={STATUS_COLORS[record.status] || "default"}
                className="capitalize px-3 py-1"
              >
                {STATUS_LABELS[record.status] || record.status}
              </Tag>
            );
          }

          return (
            <Space size="middle">
              <Popconfirm
                title="Approve Device"
                description={`Approve this device for ${
                  record.instructor_name || "the instructor"
                }? Their current device will be revoked and they will be notified.`}
                onConfirm={() => handleApprove(record._id)}
                okText="Yes, Approve"
                cancelText="Cancel"
                okButtonProps={{ type: "primary" }}
              >
                <Button type="primary" size="small">
                  Approve
                </Button>
              </Popconfirm>
              <Popconfirm
                title="Reject Device"
                description={`Reject this device for ${
                  record.instructor_name || "the instructor"
                }? They will not be able to check in from it.`}
                onConfirm={() => handleReject(record._id)}
                okText="Yes, Reject"
                cancelText="Cancel"
                okButtonProps={{ danger: true }}
              >
                <Button type="primary" danger size="small">
                  Reject
                </Button>
              </Popconfirm>
            </Space>
          );
        },
      },
    ],
    [handleApprove, handleReject]
  );

  const auditColumns = useMemo(
    () => [
      {
        title: "When",
        dataIndex: "createdAt",
        key: "createdAt",
        align: "center",
        render: (text) => (
          <span className="text-gray-600">
            {text ? moment(text).format("DD/MM/YYYY hh:mm A") : "-"}
          </span>
        ),
      },
      {
        title: "Instructor",
        dataIndex: "instructor_name",
        key: "instructor",
        align: "center",
        render: (text) => <div className="capitalize">{text || "Unknown"}</div>,
      },
      {
        title: "Event",
        dataIndex: "action",
        key: "action",
        align: "center",
        render: (action) => {
          const meta = AUDIT_LABELS[action] || {
            text: action,
            color: "default",
          };
          return (
            <Tag color={meta.color} className="px-3 py-1">
              {meta.text}
            </Tag>
          );
        },
      },
      {
        title: "Device",
        key: "device",
        align: "center",
        render: (_, record) => (
          <div className="flex flex-col">
            <Tooltip title={record.device_id}>
              <span className="text-xs text-gray-600 cursor-help">
                {shortId(record.device_id)}
              </span>
            </Tooltip>
            {record.previous_device_id && (
              <Tooltip title={record.previous_device_id}>
                <span className="text-xs text-gray-400 cursor-help">
                  was {shortId(record.previous_device_id)}
                </span>
              </Tooltip>
            )}
          </div>
        ),
      },
      {
        title: "Detail",
        key: "reason",
        align: "center",
        render: (_, record) => (
          <span className="text-gray-600">
            {AUDIT_REASONS[record.reason] || record.reason || "-"}
            {record.notes ? ` — ${record.notes}` : ""}
          </span>
        ),
      },
    ],
    []
  );

  return (
    <div className="mt-8">
      <div className="bg-white drop-shadow-md p-8 rounded-lg">
        <div className="text-2xl text-orange-500 font-semibold py-4">
          Admin - Device Management
        </div>
        <div className="text-gray-500 pb-4">
          Instructors can only check in from a device registered to them. Review
          requests here when someone changes phone or reinstalls the app.
        </div>

        <div className="flex items-center gap-4 pb-4">
          <span className="text-gray-600">Status</span>
          <Select
            value={statusFilter}
            onChange={setStatusFilter}
            style={{ width: 180 }}
            allowClear
            placeholder="All statuses"
          >
            <Option value="pending">Pending</Option>
            <Option value="active">Active</Option>
            <Option value="rejected">Rejected</Option>
            <Option value="revoked">Revoked</Option>
          </Select>
        </div>

        <Table
          columns={columns}
          dataSource={data}
          loading={isLoading}
          rowKey={(record) => record._id}
          pagination={{ pageSize: 10 }}
          locale={{ emptyText: "No device requests" }}
        />
      </div>

      <div className="bg-white drop-shadow-md p-8 rounded-lg mt-8">
        <div className="text-2xl text-orange-500 font-semibold py-4">
          Device Activity
        </div>
        <div className="text-gray-500 pb-4">
          Every registration, approval, rejection and blocked check-in attempt,
          newest first.
        </div>

        <Table
          columns={auditColumns}
          dataSource={auditRows}
          loading={auditLoading}
          rowKey={(record) => record._id}
          pagination={{ pageSize: 10 }}
          locale={{ emptyText: "No device activity yet" }}
        />
      </div>
    </div>
  );
};

export default Devices;
